import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import logo from "@/assets/4FaceMedia_Wortmarke_MagicWhite.svg";

const navLinks = [
  { label: "Home", path: "/" },
  { label: "Leistungen", path: "/leistungen" },
  { label: "Projekte", path: "/projekte" },
  { label: "Kalender", path: "/kalender" },
  { label: "Über uns", path: "/ueber-uns" },
  { label: "Kontakt", path: "/kontakt" },
];

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [visible, setVisible] = useState(true);
  const location = useLocation();

  useEffect(() => {
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 50);
      setVisible(y < 50 || y < lastY);
      lastY = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled && !mobileOpen ? "glass py-3" : "bg-transparent py-6"
        } ${visible || mobileOpen ? "translate-y-0" : "-translate-y-full"}`}
      >
        <div className="container mx-auto flex items-center justify-between px-6">
          {/* Logo */}
          <Link to="/" className="relative z-[60]">
            <img src={logo} alt="4FaceMedia" className="h-7" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8 font-agency">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-lg font-bold italic uppercase tracking-wide transition-all duration-300 relative group ${
                  location.pathname === link.path
                    ? "text-foreground"
                    : "text-foreground/60 hover:text-foreground"
                }`}
              >
                {link.label}
                <span
                  className={`absolute -bottom-1 left-0 h-[2px] bg-primary transition-all duration-300 ${
                    location.pathname === link.path ? "w-full" : "w-0 group-hover:w-full"
                  }`}
                />
              </Link>
            ))}
          </nav>

          {/* Mobile Toggle — always above overlay */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden text-foreground p-2 relative z-[60]"
            aria-label={mobileOpen ? "Menü schließen" : "Menü öffnen"}
          >
            {mobileOpen ? <X size={28} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      {/* Mobile Fullscreen Overlay — separate from header */}
      <div
        className={`md:hidden fixed inset-0 z-[55] flex flex-col items-center justify-start pt-28 transition-all duration-500 ${
          mobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        style={{
          backgroundColor: mobileOpen ? "hsl(var(--background) / 0.92)" : "transparent",
          backdropFilter: mobileOpen ? "blur(24px)" : "none",
          WebkitBackdropFilter: mobileOpen ? "blur(24px)" : "none",
        }}
      >
        <nav className="flex flex-col items-center gap-10">
          {navLinks.map((link, i) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setMobileOpen(false)}
              className={`text-3xl font-agency font-bold italic uppercase tracking-wider transition-all duration-700 ease-out ${
                location.pathname === link.path ? "text-primary" : "text-foreground/70 hover:text-foreground"
              }`}
              style={{
                transitionDelay: mobileOpen ? `${i * 120}ms` : "0ms",
                transform: mobileOpen
                  ? "translateX(0) scale(1)"
                  : "translateX(-40px) scale(0.9)",
                opacity: mobileOpen ? 1 : 0,
                filter: mobileOpen ? "blur(0px)" : "blur(6px)",
              }}
            >
              {link.label}
            </Link>
          ))}
        </nav>
      </div>
    </>
  );
};

export default Header;
